import { Box, Typography } from '@mui/material';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import SummaryLine from 'src/components/ui-component/form-component/abschluss-page/SummaryLine';
import { useApplication } from 'src/context/ApplicationContext';
import { AUSGABE_KATEGORIE } from 'src/lib/constants/constant';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import ApplicationHeading from '../../../application-heading/ApplicationHeading';

// import schemas
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import VerwendungsnachweissSchema from 'src/lib/paths/verwendungsnachweis_FULL_Paths.json';
const VN_P = VerwendungsnachweissSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

const BescheidDaten: FC<{}> = () => {
	const { application } = useApplication();

	const summe = useMemo(() => {
		const allKostenPositionen = get(application, VN_P.Foerderantrag.schema.attributes.Foerderantrag_Kostenpositions.path, []);

		const summeLadeInfrastruktur = allKostenPositionen
			.filter(
				(ele: any) =>
					get(ele, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Ausgabekategorie.path, '') ===
					AUSGABE_KATEGORIE.LadeTankInfrastruktur
			)
			.reduce((acc: number, curr: any) => acc + get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0), 0);

		const summeNetzanschluss = allKostenPositionen
			.filter(
				(ele: any) =>
					get(ele, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Ausgabekategorie.path, '') ===
					AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten
			)
			.reduce((acc: number, curr: any) => acc + get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0), 0);

		const summeEnergieSpeicher = allKostenPositionen
			.filter(
				(ele: any) =>
					get(ele, KP_P.Allgemein_Kostenkategorie.schema.attributes.Allgemein_Ausgabekategorie.path, '') === AUSGABE_KATEGORIE.Energiespeicher
			)
			.reduce((acc: number, curr: any) => acc + get(curr, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0), 0);

		return {
			[AUSGABE_KATEGORIE.LadeTankInfrastruktur]: summeLadeInfrastruktur,
			[AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten]: summeNetzanschluss,
			[AUSGABE_KATEGORIE.Energiespeicher]: summeEnergieSpeicher,
			gesamt: summeLadeInfrastruktur + summeNetzanschluss + summeEnergieSpeicher,
		};
	}, [application]);

	const verfuegbareZuwendung = useMemo(() => {
		const bewilligteZuwendung = get(application, VN_P.Foerderantrag.schema.attributes.Bewilligung_Bewilligte_Zuwendung.path, 0);
		const bereitAbgerufeneZuwendung = get(application, VN_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, 0);

		return Math.max(0, bewilligteZuwendung - bereitAbgerufeneZuwendung);
	}, [application]);

	return (
		<>
			<ApplicationHeading title={'Folgende Projektdaten sind lt. Bescheid bewilligt und zu berücksichtigen:'} />

			<Box sx={{ mt: '2rem' }}>
				<SummaryLine
					label={'Zuwendungsfähige Gesamtausgaben bis zu:'}
					alignValueRight
					value={DisplayEuro(get(application, VN_P.Foerderantrag.schema.attributes.Allgemein_Geplante_Projektausgaben.path, 0))}
				/>

				<Typography sx={{ paddingLeft: '0.5rem', mt: '1rem', fontWeight: 600 }}>davon</Typography>

				<SummaryLine
					alignValueRight
					labelPadding='0.75rem 2rem'
					label={'Ausgaben Ladeinfrastruktur bis zu:'}
					value={DisplayEuro(summe[AUSGABE_KATEGORIE.LadeTankInfrastruktur])}
				/>
				<SummaryLine
					alignValueRight
					labelPadding='0.75rem 2rem'
					label={'Ausgaben Netzanschluss/Erdarbeiten bis zu:'}
					value={DisplayEuro(summe[AUSGABE_KATEGORIE.NetzanschlussUndErdarbeiten])}
				/>
				<SummaryLine
					alignValueRight
					labelPadding='0.75rem 2rem'
					label={'Ausgaben Energiespeicher bis zu:'}
					value={DisplayEuro(summe[AUSGABE_KATEGORIE.Energiespeicher])}
				/>

				<Box sx={{ mt: '2rem' }}>
					<SummaryLine
						alignValueRight
						labelPadding='0.75rem 2rem'
						label={'Eigenmittel gesamt:'}
						value={DisplayEuro(get(application, VN_P.Foerderantrag.schema.attributes.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path, 0))}
					/>
				</Box>

				<Box sx={{ mt: '2rem' }}>
					<SummaryLine
						alignValueRight
						label={'Förderquote: '}
						value={DisplayDecimal(get(application, VN_P.Foerderantrag.schema.attributes.Allgemein_Foerderquote_Gesamt.path, 0), true)}
					/>
					<SummaryLine
						alignValueRight
						label={'Bewilligte Förderung: '}
						value={DisplayEuro(get(application, VN_P.Foerderantrag.schema.attributes.Bewilligung_Bewilligte_Zuwendung.path, 0))}
					/>
					<SummaryLine alignValueRight label={'Noch verfügbare Fördermittel: '} value={DisplayEuro(verfuegbareZuwendung)} />
				</Box>
				<Box sx={{ mt: '4rem' }}>
					<Typography>
						Bitte beachten Sie die Informationen zur Erstellung von Mittelabruf und Verwendungsnachweis sowie den FAQ-Bereich im Menüpunkt
						„Wichtige Hinweise“.
					</Typography>
				</Box>
			</Box>
		</>
	);
};

export default BescheidDaten;
